/**
 * WebSocket Management Module
 * Handles WebSocket connections for real-time chat
 */

import { StoreApi } from 'zustand';
import { ChatState, WebSocketMessage } from './types';
import { Message, ExecutionUpdate } from '../../types/chat';

// Message classification helper using discriminated patterns
type MessageClassification = 'execution_summary' | 'raw_json' | 'regular_message';

function classifyMessage(content: string): MessageClassification {
  // Check for execution summary markers
  if (content.includes('**Plan Execution') &&
      (content.includes('**Task Results:**') || content.includes('📊 **Summary:**'))) {
    return 'execution_summary';
  }

  // Check if it's pure JSON
  const trimmed = content.trim();
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed);
      return 'raw_json';
    } catch {
      // Not valid JSON, treat as regular message
    }
  }

  return 'regular_message';
}

// Format execution content to properly display JSON data
function formatExecutionContent(content: string): string {
  // Check if content contains raw JSON in task results
  if (content.includes('**Task Results:**') || content.includes('**Summary:**')) {
    // More flexible regex to match JSON objects in the results
    return content.replace(/\{[^{}]*"data":\s*\[[^\]]*\][^{}]*"status":\s*"success"[^{}]*\}/gm, (match) => {
      try {
        const parsed = JSON.parse(match);
        if (parsed.data && Array.isArray(parsed.data)) {
          // Format the data nicely
          if (parsed.data.length === 0) {
            return 'No data returned';
          }

          // Group items by their parent/type
          const cashItems: string[] = [];
          const bankItems: string[] = [];
          const otherItems: string[] = [];

          parsed.data.forEach((item: any) => {
            // Format based on the parent group
            if (item.PARENT === 'Cash-in-Hand' && item.CLOSINGBALANCE) {
              cashItems.push(`    • ${item.NAME}: ₹${Number(item.CLOSINGBALANCE).toLocaleString('en-IN')}`);
            } else if (item.PARENT === 'Bank Accounts' && item.CLOSINGBALANCE) {
              bankItems.push(`    • ${item.NAME}: ₹${Number(item.CLOSINGBALANCE).toLocaleString('en-IN')}`);
            } else if (item.NAME && !item.PARENT) {
              // Company names
              otherItems.push(`    • ${item.NAME}`);
            }
          });

          // Build formatted output
          let output = '';

          if (otherItems.length > 0) {
            output = `  Companies found:\n${otherItems.join('\n')}`;
          }

          if (cashItems.length > 0 || bankItems.length > 0) {
            output = '  Ledgers retrieved:\n';
            if (cashItems.length > 0) {
              output += `  💵 Cash Accounts:\n${cashItems.join('\n')}\n`;
            }
            if (bankItems.length > 0) {
              output += `  🏦 Bank Accounts:\n${bankItems.join('\n')}`;
            }
          }

          return output || `  ${parsed.data.length} items retrieved`;
        }
        return match; // Return original if structure is unexpected
      } catch (e) {
        console.error('Failed to parse JSON in execution result:', e);
        // Try to at least make it more readable
        return match.replace(/,/g, ',\n  ');
      }
    });
  }

  return content;
}

export const createWebSocketHandlers = (
  set: StoreApi<ChatState>['setState'],
  get: StoreApi<ChatState>['getState']
) => ({
  connectWebSocket: () => {
    const state = get();
    
    // Disconnect existing WebSocket if any
    if (state.ws) {
      console.log('Closing existing WebSocket connection');
      state.ws.close();
      set({ ws: null, isConnected: false });
    }
    
    // Check for session with more detailed logging
    if (!state.currentSession?.id && !state.sessionId) {
      console.warn('Cannot connect WebSocket: No active session found');
      console.warn('Current session:', state.currentSession);
      console.warn('Session ID:', state.sessionId);
      console.warn('Full state:', {
        hasCurrentSession: !!state.currentSession,
        sessionId: state.sessionId,
        messagesLength: state.messages.length
      });
      return;
    }
    
    const sessionId = state.currentSession?.id || state.sessionId;
    if (!sessionId) {
      console.warn('Cannot connect WebSocket: No session ID available');
      console.warn('Session object:', state.currentSession);
      return;
    }
    
    console.log('Attempting WebSocket connection with session:', sessionId);
    console.log('Current messages in store:', state.messages.length);
    
    try {
      // Connect to AA WebSocket API endpoint with token in query parameter
      const baseUrl = import.meta.env.VITE_WS_URL;
      if (!baseUrl) {
        console.error('VITE_WS_URL is not configured - WebSocket cannot connect');
        console.error('Please set VITE_WS_URL environment variable for chat functionality');
        get().setConnected(false);
        return;
      }
      const token = localStorage.getItem('auth_token');
      // CRITICAL: WebSocket URL must include /api/ prefix to match nginx routing
      const wsUrl = `${baseUrl}/api/chat/ws?session_id=${sessionId}${token ? `&token=${encodeURIComponent(token)}` : ''}`;

      console.log('Connecting to AccountsGenie Chat WebSocket (independent of TC status):', wsUrl.replace(/token=[^&]+/, 'token=***'));
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('✅ AccountsGenie Chat WebSocket connected successfully');
        console.log('Chat is now available for general queries and Tally operations (when TC is connected)');
        get().setConnected(true);
        
        // Send initial status request using backend's expected format
        try {
          ws.send(JSON.stringify({
            type: 'get_status'
          }));
        } catch (error) {
          console.error('Failed to send initial status request:', error);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          // WebSocket message received
          
          switch (message.type) {
            case 'message': {
              // Handle incoming chat message
              if (!message.message) break;

              const { content, id, role, timestamp, messageType, visualizationData } = message.message as any;

              // Handle visualization messages from agent
              if (messageType === 'visualization' && visualizationData) {
                const vizMessage: Message = {
                  id: id || crypto.randomUUID(),
                  content: typeof content === 'string' ? content : '',
                  role,
                  timestamp: new Date(timestamp || Date.now()),
                  status: 'sent',
                  messageType: 'visualization',
                  visualizationData: visualizationData
                };
                get().addMessage(vizMessage);
                set({ isLoading: false, canInterrupt: false, executionState: 'completed' });
                break;
              }

              // Type guard: Check if content is a string
              if (typeof content !== 'string') {
                // Non-string content (e.g., objects) - skip
                break;
              }

              // Format JSON in execution summaries before classification
              const formattedContent = formatExecutionContent(content);

              // Message type discrimination based on content patterns
              const messageClassification = classifyMessage(formattedContent);

              switch (messageClassification) {
                case 'execution_summary': {
                  // This is the formatted execution summary from AccountsGenie
                  // Store it to prevent duplicate ExecutionResult component
                  (get() as any).lastExecutionSummary = formattedContent;

                  // Add as regular message for display
                  const summaryMessage: Message = {
                    id: id || crypto.randomUUID(),
                    content: formattedContent,
                    role,
                    timestamp: new Date(timestamp || Date.now()),
                    status: 'sent'
                  };
                  get().addMessage(summaryMessage);
                  // Reset loading and interrupt state when response arrives
                  set({ isLoading: false, canInterrupt: false, executionState: 'completed' });
                  break;
                }

                case 'raw_json': {
                  // Skip pure JSON responses that shouldn't be displayed
                  console.debug('Skipping raw JSON message');
                  break;
                }

                case 'regular_message': {
                  // Normal text message - add to chat
                  const textMessage: Message = {
                    id: id || crypto.randomUUID(),
                    content: formattedContent,
                    role,
                    timestamp: new Date(timestamp || Date.now()),
                    status: 'sent'
                  };
                  // Complete execution and get stats before adding message
                  const stepState = get().stepProgress;
                  let stepSummary: { steps: number; duration: number } | undefined;
                  if (stepState.lastCompletedStep > 0 && stepState.startTime) {
                    stepSummary = get().completeExecution();
                  }

                  // Attach step summary to the message
                  const messageWithSteps = stepSummary
                    ? { ...textMessage, stepSummary }
                    : textMessage;

                  get().addMessage(messageWithSteps);
                  // Reset loading and interrupt state when response arrives
                  set({ isLoading: false, canInterrupt: false, executionState: 'completed' });
                  break;
                }
              }
              break;
            }

            case 'plan':
              // Handle execution plan - add as a message in chat
              if (message.plan) {
                set({
                  currentPlan: message.plan,
                  hasExecutionCompleted: false // Reset when new plan arrives
                });
                
                // Add plan as a special message type in the chat
                const planMessage: Message = {
                  id: crypto.randomUUID(),
                  content: '', // Content not used for plan type
                  role: 'system',
                  timestamp: new Date(),
                  status: 'sent',
                  messageType: 'plan',
                  planData: message.plan
                };
                get().addMessage(planMessage);
              }
              break;
              
            case 'execution_update':
              // Handle execution progress update
              // Received execution_update
              if (message.task_id && message.status !== undefined && message.progress !== undefined) {
                const update: ExecutionUpdate = {
                  task_id: message.task_id,
                  status: message.status as any,
                  progress: message.progress,
                  result: message.result,
                  error: message.error
                };
                // Adding execution update to store
                get().addExecutionUpdate(update);
                
                // Set execution state
                if (message.status === 'started') {
                  set({ isExecuting: true });
                }
              } else {
                // Incomplete execution_update message
              }
              break;
              
            case 'execution_started': {
              // Handle execution start - just update state, don't add a message
              // The ExecutionMonitor will be shown with the plan approval
              const startTime = Date.now();
              set({
                isExecuting: true,
                executionUpdates: [], // Clear previous execution updates for new plan
                executionStartTime: startTime
              });

              // Don't add a separate execution message - the monitor is already shown with the plan
              break;
            }
              
            case 'execution_complete': {
              // Handle execution completion - create a formatted result message
              console.log('🔍 EXECUTION_COMPLETE MESSAGE:', JSON.stringify(message, null, 2));

              // Parse execution data to create structured result
              const executionResult = (message as any).result || (message as any).execution_result;

              // Check if we've already processed completion for this execution
              const currentState = get();
              if (currentState.hasExecutionCompleted) {
                // Already processed completion, just update state and exit
                console.log('🔍 SKIPPING duplicate execution_complete message - already processed');
                set({ isExecuting: false });
                break;
              }

              // Skip execution_complete messages that don't have proper execution data
              // These are likely just status updates, not the main result
              // ONLY process messages that have either formatted_summary OR a complete execution_result with tasks
              if (!executionResult ||
                  (!executionResult.formatted_summary &&
                   (!executionResult.execution_result || !executionResult.execution_result.tasks))) {
                console.log('🔍 SKIPPING execution_complete message - no formatted_summary and no execution_result.tasks');
                console.log('🔍 SKIPPED MESSAGE STRUCTURE:', Object.keys(executionResult || {}));
                set({ isExecuting: false });
                break;
              }

              // Extract the formatted summary from the backend
              const formattedSummary = executionResult?.formatted_summary;

              // Debug logging to see what we're getting
              console.log('🔍 EXTRACTED formattedSummary:', formattedSummary);
              console.log('🔍 EXECUTION RESULT STRUCTURE:', Object.keys(executionResult || {}));
              console.log('🔍 FULL EXECUTION RESULT:', executionResult);

              // Extract tasks from the execution result
              let tasks = [];
              if (executionResult?.execution_result?.tasks) {
                tasks = executionResult.execution_result.tasks.map((task: any, index: number) => {
                  return {
                    id: task.task_id || `task_${index + 1}`,
                    step: parseInt(task.task_id) || (index + 1),
                    description: task.description || `Step ${index + 1}`,
                    tool: task.tool_used || undefined,
                    status: task.status === 'Completed' ? 'completed' as const :
                            task.status === 'Failed' ? 'failed' as const :
                            'skipped' as const,
                    result: task.result?.content || task.result,
                    error: task.error_message || undefined,
                    execution_time_ms: task.execution_time_ms || undefined
                  };
                });
              }

              const hasFailures = tasks.some(t => t.status === 'failed');
              const hasSkipped = tasks.some(t => t.status === 'skipped');
              const overallStatus = hasFailures ? 'failed' : hasSkipped ? 'partial' : 'success';

              // Calculate total execution time if possible
              const startTime = get().executionStartTime || Date.now();
              const totalExecutionTime = Date.now() - startTime;

              // Log to see what we're actually receiving
              if (executionResult) {
                console.log('📊 EXECUTION RESULT DATA:', executionResult);
                console.log('📝 FORMATTED SUMMARY:', formattedSummary);
              }

              const messageId = crypto.randomUUID();
              const currentPlan = get().currentPlan;

              // Create a message with the execution results using the new component format
              const resultMessage: Message = {
                id: messageId,
                content: 'Execution completed',
                role: 'assistant',
                timestamp: new Date(),
                status: 'sent',
                messageType: 'execution-result',
                executionResultData: {
                  planId: executionResult?.plan_id || currentPlan?.id || messageId,
                  planDescription: `Executed ${tasks.length} tasks successfully`,  // Keep simple description
                  tasks: tasks,
                  totalExecutionTime: executionResult?.execution_time || totalExecutionTime,
                  completedAt: new Date().toISOString(),
                  overallStatus: overallStatus as 'success' | 'partial' | 'failed',
                  // CRITICAL FIX: Always set actualResult with formatted_summary when available
                  actualResult: formattedSummary ? {
                    formatted_summary: formattedSummary
                  } : (executionResult || {}),
                  statistics: executionResult?.statistics || executionResult?.stats
                }
              };

              console.log('🎯 FINAL MESSAGE BEING CREATED:', {
                planDescription: `Executed ${tasks.length} tasks successfully`,
                actualResult: formattedSummary ? { formatted_summary: formattedSummary } : executionResult,
                hasFormattedSummary: !!formattedSummary
              });

              get().addMessage(resultMessage);
              set({
                isExecuting: false,
                hasExecutionCompleted: true, // Mark execution as completed to keep monitor visible
                executionUpdates: [] // Clear execution updates after creating result message
              });
              break;
            }
              
            case 'error': {
              // Handle error
              console.error('WebSocket error:', message.error);

              // Add error message to chat
              const errorMessage: Message = {
                id: crypto.randomUUID(),
                content: `Error: ${message.error}`,
                role: 'system',
                timestamp: new Date(),
                status: 'sent',
                error: message.error
              };
              get().addMessage(errorMessage);
              break;
            }
              
            case 'status': {
              // Check if this is an agent status message (has message field) or connection status
              if (message.message) {
                const msg = message.message as string;

                // Check if this is a step progress message
                const stepStartMatch = msg.match(/^⏳ Step (\d+)/);
                const stepDoneMatch = msg.match(/^✅ Step (\d+) done/);
                const stepFailMatch = msg.match(/^❌ Step (\d+) failed/);

                if (stepStartMatch) {
                  // Step started - update progress, don't add to chat
                  const stepNum = parseInt(stepStartMatch[1], 10);
                  get().updateStepProgress(stepNum, 'started');
                } else if (stepDoneMatch) {
                  // Step completed - update progress, don't add to chat
                  const stepNum = parseInt(stepDoneMatch[1], 10);
                  get().updateStepProgress(stepNum, 'done');
                } else if (stepFailMatch) {
                  // Step failed - update progress, don't add to chat
                  const stepNum = parseInt(stepFailMatch[1], 10);
                  get().updateStepProgress(stepNum, 'failed');
                } else {
                  // Non-step status message - add to chat as before
                  const statusMessage: Message = {
                    id: crypto.randomUUID(),
                    content: msg,
                    role: 'system',
                    timestamp: new Date(),
                    status: 'sent',
                    messageType: 'agent-status'
                  };
                  get().addMessage(statusMessage);
                }
              } else if (message.connected !== undefined) {
                // Connection status
                get().setConnected(message.connected);
              }
              break;
            }

            case 'execution_state': {
              // Handle execution state changes (like Claude Code)
              // States: idle, running, interrupted, completed
              const newState = message.state || 'idle';
              console.log(`🔄 Execution state changed: ${newState}`);

              get().setExecutionState(newState as any);

              // Update canInterrupt based on state
              const canInterrupt = newState === 'running';
              set({ canInterrupt });

              // CRITICAL: Reset isExecuting when not running, so input is not disabled
              if (newState === 'interrupted' || newState === 'completed' || newState === 'idle') {
                set({ isExecuting: false });
              }

              // Show status message for interruption
              if (newState === 'interrupted' && message.message) {
                const interruptMessage = {
                  id: crypto.randomUUID(),
                  content: message.message,
                  role: 'system' as const,
                  timestamp: new Date(),
                  status: 'sent' as const,
                  messageType: 'agent-status' as const
                };
                get().addMessage(interruptMessage);
              }
              break;
            }

            case 'guidance_received': {
              // Handle confirmation that guidance was queued
              console.log('📨 Guidance message queued for processing');
              if (message.message) {
                const guidanceConfirm = {
                  id: crypto.randomUUID(),
                  content: `💡 ${message.message}`,
                  role: 'system' as const,
                  timestamp: new Date(),
                  status: 'sent' as const,
                  messageType: 'agent-status' as const
                };
                get().addMessage(guidanceConfirm);
              }
              break;
            }

            case 'todo_update': {
              // Handle agent todo list updates (from Claude SDK TodoWrite tool)
              console.log('📋 Agent todo update received:', message.todos?.length || 0, 'items');
              if (message.todos && Array.isArray(message.todos)) {
                get().setAgentTodos(message.todos);
              }
              break;
            }

            case 'new_step_added':
            case 'steps_added': {
              // Handle new steps being added during execution decomposition
              const newSteps = message.type === 'new_step_added' && message.step
                ? [message.step]
                : (message.steps || []);

              if (newSteps.length === 0) break;

              const parentStep = message.parent_step;
              const insertAfter = message.insert_after;

              console.log(`📋 Received ${newSteps.length} new step(s) to insert after step ${insertAfter}`);

              const currentState = get();

              // Update currentPlan if exists
              if (currentState.currentPlan) {
                const updatedTasks = [...currentState.currentPlan.tasks];

                // Find insertion index (after parent step)
                const insertIndex = updatedTasks.findIndex(t => t.step === insertAfter) + 1;

                // Format new steps for plan
                const formattedSteps = newSteps.map(step => ({
                  id: step.id,
                  step: step.step,
                  description: step.description,
                  tool: step.tool || '',
                  risk_level: step.risk_level || 'low',
                  status: step.status || 'pending',
                  dependencies: step.dependencies.map(d => d.toString()) || [],
                  execution_type: 'series' as const,
                  parent_step: step.parent_step
                }));

                // Insert new steps at correct position
                updatedTasks.splice(insertIndex, 0, ...formattedSteps);

                console.log(`✅ Inserted ${formattedSteps.length} steps at index ${insertIndex}`);

                set({
                  currentPlan: {
                    ...currentState.currentPlan,
                    tasks: updatedTasks
                  }
                });
              }

              // Also broadcast to ExecutionMonitor via execution updates
              newSteps.forEach(step => {
                const update: ExecutionUpdate = {
                  task_id: step.id,
                  status: 'started',
                  progress: 0,
                  metadata: {
                    is_new_step: true,
                    parent_step: parentStep,
                    inserted_at: Date.now(),
                    description: step.description,
                    tool: step.tool,
                    dependencies: step.dependencies
                  }
                };
                get().addExecutionUpdate(update);
              });

              break;
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ AccountsGenie Chat WebSocket connection error:', error);
        console.error('This affects chat functionality. Tally Connection (TC) status is separate.');
        get().setConnected(false);
      };

      ws.onclose = (event) => {
        console.log('🔌 AccountsGenie Chat WebSocket disconnected, code:', event.code, 'reason:', event.reason);
        console.log('This affects chat functionality only. TC connection status is independent.');
        get().setConnected(false);
        set({ isExecuting: false });
        
        // Only attempt to reconnect if:
        // 1. Not a deliberate close (code 1000)
        // 2. Session still exists
        // 3. Not too many consecutive failures
        const currentState = get();
        const shouldReconnect = event.code !== 1000 && 
                               event.code !== 1001 && 
                               (currentState.currentSession || currentState.sessionId);
        
        if (shouldReconnect) {
          console.log('🔄 Scheduling AccountsGenie Chat WebSocket reconnection in 3 seconds...');
          setTimeout(() => {
            const state = get();
            if (!state.ws || state.ws?.readyState === WebSocket.CLOSED) {
              console.log('🔄 Attempting AccountsGenie Chat WebSocket reconnection...');
              get().connectWebSocket();
            }
          }, 3000);
        }
      };

      set({ ws });
    } catch (error) {
      console.error('❌ Failed to establish AccountsGenie Chat WebSocket connection:', error);
      console.error('Chat functionality will be limited to HTTP fallback. TC connection is independent.');
      get().setConnected(false);
    }
  },

  disconnectWebSocket: () => {
    const { ws } = get();
    if (ws) {
      ws.close();
      set({ ws: null, isConnected: false });
    }
  },

  approvePlan: (planId: string, approved: boolean) => {
    const state = get();

    if (state.ws && state.isConnected && state.ws.readyState === WebSocket.OPEN) {
      const wsMessage = {
        type: 'approve_plan',
        plan_id: planId,
        approved: approved
      };

      state.ws.send(JSON.stringify(wsMessage));
      console.log('[WebSocket] Sent plan approval:', { planId, approved });

      // Clear the current plan since it's been processed
      if (approved) {
        set({ isExecuting: true });
        console.log('[WebSocket] Set isExecuting to true after approval');
      } else {
        set({ currentPlan: null });
      }
    } else {
      console.error('WebSocket not available for plan approval');
    }
  },

  // Interactive execution actions (like Claude Code ESC key support)
  interruptExecution: () => {
    const state = get();

    if (state.ws && state.isConnected && state.ws.readyState === WebSocket.OPEN) {
      // Send interrupt signal (ESC key equivalent)
      const wsMessage = {
        type: 'interrupt'
      };

      state.ws.send(JSON.stringify(wsMessage));
      console.log('[WebSocket] 🛑 Sent interrupt signal');

      // Optimistically update UI - IMPORTANT: Also reset isExecuting so input is not disabled
      set({
        executionState: 'interrupted',
        canInterrupt: false,
        isLoading: false,
        isExecuting: false  // Allow user to send new messages after interruption
      });
    } else {
      console.error('WebSocket not available for interrupt');
    }
  },

  sendGuidance: (content: string) => {
    const state = get();

    if (!content.trim()) {
      console.warn('Empty guidance message, ignoring');
      return;
    }

    if (state.ws && state.isConnected && state.ws.readyState === WebSocket.OPEN) {
      // Check if we're currently executing
      if (state.executionState === 'running') {
        // Add user's guidance message to chat FIRST (so it persists in UI)
        const userGuidanceMessage: Message = {
          id: crypto.randomUUID(),
          content: content.trim(),
          role: 'user',
          timestamp: new Date(),
          status: 'sent',
          messageType: 'guidance' as any // Mark as guidance for styling purposes
        };
        get().addMessage(userGuidanceMessage);

        // Send as guidance (mid-execution)
        const wsMessage = {
          type: 'guidance',
          content: content.trim()
        };

        state.ws.send(JSON.stringify(wsMessage));
        console.log('[WebSocket] 💡 Sent guidance message:', content.substring(0, 50));
      } else {
        // Not executing, send as regular message
        console.log('[WebSocket] Not executing, sending as regular message');
        get().sendMessage(content);
      }
    } else {
      console.error('WebSocket not available for guidance');
    }
  },

  setExecutionState: (newState: 'idle' | 'running' | 'interrupted' | 'completed') => {
    set({
      executionState: newState,
      canInterrupt: newState === 'running',
      isLoading: newState === 'running'
    });
  },
});